/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/**
 * Pure builder for the A2P opt-in confirmation SMS sent to a contact AT THE
 * MOMENT they're added as an emergency contact.
 *
 * Why this exists: previously the recipient's first message from MyAmbits was
 * the SOS / Walk Me Home alert itself — no prior opt-in/confirmation. Twilio
 * A2P rejected the campaign for missing "opt-in information"; carriers expect a
 * confirmation at opt-in time with STOP/HELP instructions. This message is that
 * confirmation. Sent fire-and-forget on ADD (never edit); failure never blocks
 * the add.
 *
 * Encoding: GSM-7 (no emoji / curly quotes / non-ASCII), ~173–180 chars → 2
 * segments. Stays under the /api/sms-send 320-char cap. Keep it ASCII-only so
 * it never silently flips to UCS-2 (70-char segments).
 *
 * `adderName` should be passed already resolved via senderLabel() so it's
 * never blank ("Someone you know" fallback).
 */
export function buildContactConfirmationSms(adderName: string): string {
  const name = adderName.trim() || "Someone";
  return (
    `[MyAmbits] ${name} added you as an emergency contact. ` +
    `You'll receive safety alerts only if they trigger an SOS or Walk Me Home. ` +
    `Reply HELP for help, STOP to opt out. Msg&data rates may apply.`
  );
}

/**
 * Two-step opt-in INVITATION SMS (the recipient-opt-in model TCR requires).
 *
 * Unlike buildContactConfirmationSms (the one-step model), this asks the
 * recipient to explicitly opt in by replying CONFIRM — no safety alerts are
 * sent to a `pending` contact until they confirm. (CONFIRM, not YES: YES is a
 * Twilio-reserved keyword intercepted before our webhook.) Handled inbound by
 * api/sms-inbound.ts + parseInboundKeyword. GSM-7 / ASCII-only so it never
 * flips to UCS-2; stays under the /api/sms-send 320-char cap. `adderName` should
 * be resolved via senderLabel() so it's never blank.
 */
export function buildContactInvitationSms(adderName: string): string {
  const name = adderName.trim() || "Someone";
  // CONFIRM (not YES) — YES is a Twilio-reserved opt-in keyword that Twilio's
  // Advanced Opt-Out intercepts before our webhook. STOP genuinely opts them
  // out at the carrier level (compliant + honest).
  return (
    `[MyAmbits] ${name} added you as an emergency contact. ` +
    `Reply CONFIRM to receive their safety alerts (SOS / Walk Me Home only), ` +
    `or STOP to opt out. Reply HELP for help. Msg&data rates may apply.`
  );
}
